import React from 'react';

const buttonCls =
  'min-w-9 h-9 px-3 flex items-center justify-center text-lg font-semibold rounded-md border border-gray-300 bg-[var(--card-bg)] text-gray-800 cursor-pointer transition-all duration-200 hover:bg-[var(--bg-tertiary)] hover:border-[var(--border-color)] hover:-translate-y-px hover:shadow-[var(--shadow-sm)] active:translate-y-0 active:shadow-[0_1px_2px_rgba(0,0,0,0.1)] focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] dark:text-[var(--text-primary)] dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)] dark:hover:bg-[var(--bg-tertiary)] dark:hover:border-[rgba(148,163,184,0.3)] dark:focus:border-[#60a5fa] dark:focus:shadow-[0_0_0_3px_rgba(96,165,250,0.15)]';

const UmlautButtons = ({ inputRef = null, onInsertChar = null, langCode = null }) => {
  // Only show umlaut buttons for German language (lang-code: 'de')
  if (langCode !== 'de') {
    return null;
  }

  const insertChar = (char) => {
    // If onInsertChar callback is provided, use it (preferred method)
    if (onInsertChar) {
      onInsertChar(char);
      return;
    }
    
    // If inputRef is provided, use it
    let input = null;
    if (inputRef) {
      if (typeof inputRef === 'function') {
        const refObj = inputRef();
        input = refObj?.current;
      } else {
        input = inputRef.current;
      }
    }
    
    // Fallback: find active input/textarea
    if (!input) {
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        input = activeElement;
      }
    }
    
    if (!input) return;
    
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const value = input.value || '';
    
    const newValue = value.substring(0, start) + char + value.substring(end);
    
    // Get the native value setter to properly update the value
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set || Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype,
      'value'
    )?.set;
    
    // Set the value using native setter
    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(input, newValue);
    } else {
      input.value = newValue;
    }
    
    // Create and dispatch input event
    const inputEvent = new Event('input', { bubbles: true, cancelable: true });
    
    // Create a proper target object
    const target = Object.create(Object.getPrototypeOf(input));
    Object.assign(target, input);
    target.value = newValue;
    
    Object.defineProperty(inputEvent, 'target', {
      writable: false,
      value: target
    });
    
    Object.defineProperty(inputEvent, 'currentTarget', {
      writable: false,
      value: input
    });
    
    // Dispatch the event
    input.dispatchEvent(inputEvent);
    
    // Set cursor position after inserted character
    setTimeout(() => {
      input.focus();
      const newCursorPos = start + char.length;
      if (input.setSelectionRange) {
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const umlautChars = ['ä', 'ö', 'ü', 'Ä', 'Ö', 'Ü', 'ß'];

  return (
    <div className="flex gap-1.5 flex-wrap shrink-0">
      {umlautChars.map((char) => (
        <button
          key={char}
          type="button"
          className={buttonCls}
          tabIndex={-1}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            insertChar(char);
          }}
          onMouseDown={(e) => {
            // Prevent focus loss when clicking button
            e.preventDefault();
          }}
          title={`Insert ${char}`}
        >
          {char}
        </button>
      ))}
    </div>
  );
};

export default UmlautButtons;

